/**
 * Formatting helpers for the product review summary (star rating + review count).
 *
 * Retail renders the rating with a single fraction digit ("4.0", not "4") and groups the
 * review count using the locale's separator ("1,821", not "1821"). These helpers bring the
 * Painter PDP to parity.
 *
 * Locale is passed in explicitly by the caller rather than resolved here, matching the
 * convention established by formatPrice/parsePriceComponents in priceUtils.
 */
/**
 * Formats a star rating with exactly one fraction digit.
 *
 * @param rating - Raw rating value (optional in the API model, so defaults to 0)
 * @param locale - BCP 47 locale, resolved by the caller from the localization context
 * @returns Locale-formatted rating, e.g. en-US: 4 -> "4.0", 4.5 -> "4.5"; fr-CA: 4 -> "4,0"
 */
export const formatRating = (rating = 0, locale) => {
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    }).format(rating);
};
/**
 * Formats a review count with locale-aware digit grouping.
 *
 * @param count - Raw review count (optional in the API model, so defaults to 0)
 * @param locale - BCP 47 locale, resolved by the caller from the localization context
 * @returns Locale-formatted count, e.g. en-US: 1821 -> "1,821"; fr-CA: 1821 -> "1 821"
 */
export const formatReviewCount = (count = 0, locale) => {
    return new Intl.NumberFormat(locale, {
        useGrouping: true,
    }).format(count);
};
